import SettingsIcon from "@mui/icons-material/SettingsOutlined";
import { Avatar, Box, Button, Stack, Switch, Typography } from "@mui/material";
import { useNavigate } from "react-router";
import type { ObjectChecklist, ObjectFeature } from "../../../../api/types/objectTypes";
import { allObjectFeatures } from "../../../../api/types/objectTypes";
import useClientContext from "../../../../context/useClientContext";
import { pageRoutes } from "../../../../routes";
import { objectFeatureCaptionMap } from "../../../../utilities/enumCaptions";
import ObjectFeatureIcon from "../../../common/domain-icons/ObjectFeatureIcon";
import { useObjectDefinitionContext } from "../ObjectDefinitionContext";

interface Props {
  features: ObjectFeature[];
  checklists: ObjectChecklist[];
  onChange: (features: ObjectFeature[]) => void;
}

const objectFeatureDescriptionMap: Record<ObjectFeature, string> = {
  Checklists: "Allows adding checklists with tasks",
  Notes: "Allows adding notes",
  Files: "Allows attaching files",
  PortfolioMetrics: "Allows creating metrics and metric values for Portfolio Monitoring",
  DataImport: "Allows importing data",
  Contacts: "Allows managing object-level access for external contacts",
  InternalUserPermissions: "Allows managing object-level access for internal users",
  CardFilePreview: "Allows displaying preview of the file associated with the object",
};

const ObjectSupportedFeaturesSection = ({ features, checklists, onChange }: Props) => {
  const { hasEditPermissions } = useObjectDefinitionContext();
  const { isPortalFeatureEnabled } = useClientContext();

  const navigate = useNavigate();

  const checklistsEnabled = isPortalFeatureEnabled("ObjectChecklistsEnabled");

  const handleConfigureChecklists = () => {
    navigate(pageRoutes.checklists);
  };

  return (
    <Stack spacing={1.5}>
      {allObjectFeatures
        .filter((feature) => feature !== "Checklists" || checklistsEnabled)
        .map((feature) => {
          const isEnabled = features.includes(feature);

          return (
            <Box
              key={feature}
              sx={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                <Avatar variant="rounded" sx={{ height: 32, width: 32 }}>
                  <ObjectFeatureIcon objectFeature={feature} color="action" />
                </Avatar>
                <Stack>
                  <Typography variant="subtitle2">{objectFeatureCaptionMap[feature] || feature}</Typography>
                  <Typography color="textSecondary">{objectFeatureDescriptionMap[feature]}</Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
                {feature === "Checklists" && isEnabled && (
                  <>
                    <Typography variant="caption" color="textSecondary">
                      {checklists.length} {checklists.length === 1 ? "Checklist" : "Checklists"}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="secondary"
                      startIcon={<SettingsIcon />}
                      onClick={handleConfigureChecklists}
                    >
                      {hasEditPermissions ? "Configure" : "View"}
                    </Button>
                  </>
                )}
                <Switch
                  checked={isEnabled}
                  onChange={(_, checked) => {
                    const newFeatures = checked ? [...features, feature] : features.filter((f) => f !== feature);
                    onChange(newFeatures);
                  }}
                  disabled={!hasEditPermissions}
                />
              </Stack>
            </Box>
          );
        })}
    </Stack>
  );
};

export default ObjectSupportedFeaturesSection;
